import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const IDLE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days (iter v18, Item 7)
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const timerRef = useRef(null);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    setUser(false);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      toast.warning("Sesi berakhir karena tidak ada aktivitas selama 3 hari.");
      logout();
    }, IDLE_MS);
  }, [logout]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchMe = () => api.get("/auth/me")
      .then((r) => { if (mounted) setUser(r.data); })
      .catch(() => { if (mounted) setUser(false); });
    fetchMe();
    // Re-fetch on focus so permission/role changes propagate without manual reload
    const onFocus = () => { if (mounted && user) fetchMe(); };
    window.addEventListener("focus", onFocus);
    return () => { mounted = false; window.removeEventListener("focus", onFocus); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Idle timeout — only armed when logged in
  useEffect(() => {
    if (!user) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const handler = () => resetIdleTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetIdleTimer]);

  const login = async (email, password) => {
    setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Login failed");
      return false;
    }
  };

  const hasPerm = (perm) => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    // Prefer server-computed effective_permissions (role + user override).
    const p = user.effective_permissions || user.permissions || [];
    return p.includes("*") || p.includes(perm);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, login, logout, error, hasPerm, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
